// Global configuration for file uploads and validation
import { formatBytes } from '../utils/format';

export interface FileTypeConfig {
  maxSize: number; // in bytes
  allowedTypes: string[];
  allowedExtensions: string[];
  errorMessage: string;
}

export interface UploadConfig {
  image: FileTypeConfig;
  audio: FileTypeConfig;
  video: FileTypeConfig;
  document: FileTypeConfig;
}

// File size helpers
export const MB = 1024 * 1024;
export const GB = 1024 * MB;

/**
 * The largest file one PUT to storage (R2) takes: 5 GiB minus 5 MiB. Files
 * from 100 MiB up go in parts instead (services/multipartUpload.ts).
 */
export const MAX_SINGLE_UPLOAD_BYTES = 5 * GB - 5 * MB;

/**
 * The largest file any plan takes (Pro Max). The API applies each plan's own
 * per-file limit (1 GB on Free, 10 GB on Pro) before any bytes are sent, and
 * its refusal names the limit, so the app only enforces this ceiling.
 */
export const MAX_UPLOAD_FILE_BYTES = 25 * GB;

/** How the per-plan video file limit reads everywhere in the app. */
export const VIDEO_FILE_LIMIT_COPY = 'Up to 1 GB on Free, 10 GB on Pro and 25 GB on Pro Max';

/** Editors' uploads follow the creator's plan, never below 500 MB. */
export const REVISION_FILE_LIMIT_COPY = 'up to 1 GB when the creator is on Free, 10 GB on Pro and 25 GB on Pro Max';

/** Refusal for a video over every plan's limit, before anything is uploaded. */
export const VIDEO_TOO_LARGE_COPY = 'Videos can be up to 25 GB (10 GB on Pro, 1 GB on Free).';

// Global upload configuration
export const UPLOAD_CONFIG: UploadConfig = {
  image: {
    maxSize: 5 * MB, // 5MB
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'],
    errorMessage: 'Please upload a valid image file (JPG, PNG, GIF, WebP, SVG) under 5MB'
  },
  audio: {
    maxSize: 50 * MB, // 50MB
    allowedTypes: ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/ogg', 'audio/m4a', 'audio/aac'],
    allowedExtensions: ['.mp3', '.wav', '.ogg', '.m4a', '.aac'],
    errorMessage: 'Please upload a valid audio file (MP3, WAV, OGG, M4A, AAC) under 50MB'
  },
  video: {
    // The absolute ceiling; the API refuses anything over the plan's own limit.
    maxSize: MAX_UPLOAD_FILE_BYTES,
    allowedTypes: ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm', 'video/ogg'],
    allowedExtensions: ['.mp4', '.mpeg', '.mov', '.avi', '.webm', '.ogv'],
    errorMessage: 'Please choose a video file (MP4, MOV, WebM and similar) of up to 25 GB (10 GB on Pro, 1 GB on Free).'
  },
  document: {
    maxSize: 10 * MB, // 10MB
    allowedTypes: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
    allowedExtensions: ['.pdf', '.doc', '.docx'],
    errorMessage: 'Please upload a valid document (PDF, DOC, DOCX) under 10MB'
  }
};

// Validation helpers
export const validateFile = (file: File, type: keyof UploadConfig): { valid: boolean; error?: string } => {
  const config = UPLOAD_CONFIG[type];
  
  // Check file size
  if (file.size > config.maxSize) {
    return {
      valid: false,
      error: `This file is ${formatBytes(file.size)}. Files of this type can be up to ${formatBytes(config.maxSize)}.`
    };
  }
  
  // Check file type
  const fileType = file.type.toLowerCase();
  if (!config.allowedTypes.includes(fileType)) {
    // Fallback to extension check
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!config.allowedExtensions.includes(fileExtension)) {
      return { 
        valid: false, 
        error: config.errorMessage 
      };
    }
  }
  
  return { valid: true };
};

// Format file size for display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Get file type icon based on MIME type
export const getFileTypeIcon = (fileType: string): string => {
  if (fileType.startsWith('image/')) return 'image';
  if (fileType.startsWith('audio/')) return 'audio';
  if (fileType.startsWith('video/')) return 'video';
  if (fileType.includes('pdf')) return 'pdf';
  if (fileType.includes('doc')) return 'document';
  return 'file';
};

// Storage path configuration
export const getStoragePath = (userId: string, fileType: string, fileName: string): string => {
  const timestamp = Date.now();
  const cleanFileName = fileName.replace(/[^a-zA-Z0-9.-]/g, '_');
  
  if (fileType.startsWith('image/')) {
    return `images/${userId}/${timestamp}_${cleanFileName}`;
  } else if (fileType.startsWith('audio/')) {
    return `audio/${userId}/${timestamp}_${cleanFileName}`;
  } else if (fileType.startsWith('video/')) {
    return `videos/${userId}/${timestamp}_${cleanFileName}`;
  } else {
    return `documents/${userId}/${timestamp}_${cleanFileName}`;
  }
};

// Batch upload configuration
export const BATCH_UPLOAD_CONFIG = {
  maxFiles: 10,
  maxTotalSize: 500 * MB, // 500MB total for batch uploads
  parallelUploads: 3 // Number of simultaneous uploads
};

// Asset limits for project collaboration
export const ASSET_LIMITS = {
  images: {
    maxCount: 25,
    maxDuration: 300, // 5 minutes max duration for image display
    requiresTimestamp: true,
    requiresPosition: true
  },
  audio: {
    maxCount: 5, 
    maxDuration: 600, // 10 minutes max duration for audio
    requiresTimestamp: true,
    requiresPosition: false
  },
  transitions: {
    maxCount: 50,
    maxDuration: 10, // 10 seconds max per transition
    requiresTimestamp: true,
    requiresPosition: false,
    types: [
      'Fade In/Out',
      'Slide Left/Right', 
      'Zoom In/Out',
      'Dissolve',
      'Wipe',
      'Push',
      'Spin',
      'Flip',
      'Custom'
    ]
  },
  notes: {
    maxLength: 200, // per asset note
    placeholder: 'Add specific instructions for this asset...'
  },
  effects: {
    maxPerAsset: 10, // max effects per individual asset
    intensityRange: { min: 0, max: 100, step: 5 },
    durationRange: { min: 0.1, max: 10, step: 0.1 }
  }
};

// Preview configuration
export const PREVIEW_CONFIG = {
  image: {
    maxWidth: 300,
    maxHeight: 300,
    quality: 0.8
  },
  video: {
    maxWidth: 400,
    maxHeight: 300,
    previewDuration: 10 // seconds
  }
};

// Collaboration features configuration
export const COLLABORATION_CONFIG = {
  maxAnnotationsPerFile: 50,
  maxCommentLength: 500,
  allowedAnnotationTypes: ['text', 'drawing', 'timestamp', 'region'],
  versionControl: {
    enabled: true,
    maxVersions: 10,
    autoSaveInterval: 30000 // 30 seconds
  }
};

// Editor and project assignment configuration
export const EDITOR_CONFIG = {
  // Editor statuses that are considered available for project assignment
  availableStatuses: ['active', 'invited'] as const,
  
  // Status display configuration
  statusConfig: {
    active: {
      label: 'Accepted',
      color: 'success.main',
      description: 'Editor has accepted your invitation and is available for projects'
    },
    invited: {
      label: 'Invited',
      color: 'warning.main', 
      description: 'Editor has been invited but hasn\'t responded yet'
    },
    pending: {
      label: 'Pending',
      color: 'info.main',
      description: 'Invitation is being processed'
    },
    declined: {
      label: 'Declined',
      color: 'error.main',
      description: 'Editor has declined your invitation'
    }
  },

  // PithPlay Choice service configuration
  //
  // DISABLED until the backend implements it. The UI promised "Guaranteed
  // assignment within 24 hours" and quoted a $25 fee, but nothing reads the
  // pithplayChoice flag: no editor is assigned, no fee is charged, no admin
  // is alerted and no email is sent. A project submitted this way sat at
  // VIDEO_SUBMITTED forever while the creator waited on a guarantee nobody
  // was going to keep. Re-enable once assignment actually exists.
  pithplayChoice: {
    enabled: false,
    fee: 25, // USD
    currency: 'USD',
    assignmentTimeGuarantee: 24, // hours
    benefits: [
      'Professional editor matching based on project requirements',
      'Guaranteed assignment within 24 hours',
      'Quality assurance and support',
      'Priority handling of your project'
    ],
    feeNote: 'Additional fee applies if not covered under your subscription plan'
  },

  // Editor limits
  limits: {
    maxEditorsPerUser: 25,
    maxActiveProjects: 10
  }
};

// Project configuration and limits
export const PROJECT_CONFIG = {
  // Maximum total file size for all assets in a project
  maxTotalAssetsSize: 1 * GB, // 1GB total for all project assets combined
  
  // Maximum number of edits/revisions allowed per project
  maxEditRevisions: 2, // After 2 revisions, project must be completed or cancelled
  
  // Project deadline configuration
  deadline: {
    default: 7, // days
    minimum: 1, // days
    maximum: 30, // days
  },
  
  // Storage plan limits (for future implementation)
  storagePlans: {
    basic: {
      name: 'Basic',
      storage: 1 * GB,
      description: 'Perfect for occasional creators'
    },
    premium: {
      name: 'Premium',
      storage: 100 * GB,
      description: 'For regular content creators'
    },
    enterprise: {
      name: 'Enterprise',
      storage: 200 * GB,
      description: 'For professional production teams'
    }
  },
  
  // Project status workflow
  statusWorkflow: {
    maxDaysInReview: 3, // Days before auto-escalation
    maxDaysInEditing: 14, // Days before warning sent
    autoArchiveAfterDays: 90 // Days after completion before archiving
  }
};